import { Injectable } from '@angular/core';
import { PdfRenderContext } from './pdf-render-context';
import { PdfBuilderUtilitiesService } from './pdf-builder-utilities.service';
import jsPDF from 'jspdf';

@Injectable({
  providedIn: 'root'
})
export class PdfBuilderRenderBlocksService {
  private context!: PdfRenderContext;

  constructor(private pdfUtilities: PdfBuilderUtilitiesService) { }

  public initialize(context: PdfRenderContext): void {
    this.context = context;
  }

  public renderTextBlock(
    label: string,
    content: string,
    style: 'bold' | 'italic' | 'underline' | 'normal' = 'bold',
    options?: {
      inlineLabel?: boolean;
      contentSpacing?: number;
    }
  ): void {
    const { doc, leftMargin, rightMargin, inputTextFont, headingFont } = this.context;
  
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = pageWidth - leftMargin - rightMargin;
    const fallbackText = 'Ninguno';
    const displayContent = content?.trim().length > 0 ? content : fallbackText;
    const spacing = options?.contentSpacing ?? 0;
  
    this.addNewPageIfNeeded(20);
  
    // Choose font
    let labelFont: [string, string];
    if (style === 'bold') {
      labelFont = headingFont;
    } else if (style === 'italic') {
      labelFont = [inputTextFont[0], 'italic'];
    } else {
      labelFont = inputTextFont;
    }
  
    // CASE 1: No label, just content
    if (!label || label.trim().length === 0) {
      doc.setFont(...inputTextFont);
      const lines = doc.splitTextToSize(displayContent, textWidth - spacing);
      lines.forEach(line => {
        doc.text(line, leftMargin + spacing, this.context.currentY);
        this.context.currentY += 5;
        this.addNewPageIfNeeded(20);
      });
      this.context.currentY += 3;
      return;
    }
  
    // CASE 2: Inline label + value
    if (options?.inlineLabel) {
      const labelText = label.trim();
      const labelWidth = doc.getTextWidth(labelText);
      const availableWidth = textWidth - labelWidth - 2;
      const lines = doc.splitTextToSize(displayContent, availableWidth);
  
      doc.setFont(...labelFont);
      doc.text(labelText, leftMargin, this.context.currentY);
  
      if (style === 'underline') {
        doc.setLineWidth(0.3);
        doc.line(leftMargin, this.context.currentY + 1.3, leftMargin + labelWidth, this.context.currentY + 1.3);
      }
  
      doc.setFont(...inputTextFont);
      doc.text(lines[0], leftMargin + labelWidth + 4, this.context.currentY);
      this.context.currentY += 5;
      this.addNewPageIfNeeded(20);
  
      for (let i = 1; i < lines.length; i++) {
        doc.text(lines[i], leftMargin + spacing, this.context.currentY);
        this.context.currentY += 5;
        this.addNewPageIfNeeded(20);
      }
  
      this.context.currentY += 3;
      return;
    }
  
    // CASE 3: Block label + content
    doc.setFont(...labelFont);
    doc.text(label, leftMargin, this.context.currentY);
  
    if (style === 'underline') {
      const labelWidth = doc.getTextWidth(label);
      doc.setLineWidth(0.3);
      doc.line(leftMargin, this.context.currentY + 1.3, leftMargin + labelWidth, this.context.currentY + 1.3);
    }
  
    this.context.currentY += 6;
    this.addNewPageIfNeeded(20);
  
    doc.setFont(...inputTextFont);
    const lines = doc.splitTextToSize(displayContent, textWidth - spacing);
    lines.forEach(line => {
      doc.text(line, leftMargin + spacing, this.context.currentY);
      this.context.currentY += 5;
      this.addNewPageIfNeeded(20);
    });
  
    this.context.currentY += 3;
  }
  
  public renderSectionTitle(title: string): void {
    const { doc, currentY, leftMargin, rightMargin, headingFont, inputFontSize } = this.context;
  
    const sectionHeight = 7;
    const radius = 2; // Optional: for rounded corners
  
    const pageWidth = doc.internal.pageSize.getWidth();
    const sectionWidth = pageWidth - leftMargin - rightMargin;
  
    // Light gray background
    doc.setFillColor(240, 240, 240);
  
    // Rounded rectangle behind the title
    doc.roundedRect(leftMargin, currentY - sectionHeight + 2, sectionWidth, sectionHeight, radius, radius, 'F');
  
    // Set font and draw text
    doc.setFont(...headingFont);
    doc.setFontSize(inputFontSize);
    doc.setTextColor(0, 0, 0);
  
    doc.text(title, leftMargin + 2, currentY); // small indent
  
    this.context.currentY += sectionHeight + 2; // update shared currentY
  }

  public renderLabelValueGrid(
    entries: { label: string; value: string }[],
    options?: {
      groupByLabels?: string[][];
      numColumns?: number;
      columnWidth?: number;
      rowSpacing?: number;
      drawBorders?: boolean;
      borderColumnSpacing?: number;
      borderRowSpacing?: number;
      paddingLeft?: number;
      drawInnerDivider?: boolean;
    }
  ): void {
    const { doc, leftMargin, rightMargin, inputFontSize, inputTextFont, subheadingFont } = this.context;
  
    const columnWidth = options?.columnWidth ?? 90;
    const rowSpacing = options?.rowSpacing ?? 3;
    const numCols = options?.numColumns ?? 2;
    const labelValueSpacing = 4;
    const wrapBuffer = 3;
    const lineHeight = 6;
    const paddingLeft = options?.paddingLeft ?? 0;
    const borderColumnSpacing = options?.borderColumnSpacing ?? 0;
    // const verticalAlign = lineHeight * 0.25;
    const verticalAlign = options?.drawBorders ? rowSpacing * 0.33 : 0;

    doc.setFontSize(inputFontSize);
    this.addNewPageIfNeeded(20);
  
    // --- Group entries into rows ---
    const rows: { label: string; value: string }[][] = [];
  
    if (options?.groupByLabels) {
      for (const group of options.groupByLabels) {
        const row: { label: string; value: string }[] = [];
        for (const label of group) {
          const entry = entries.find(e => e.label === label);
          if (entry) {
            row.push({ label: String(entry.label), value: String(entry.value) });
          }
        }
        if (row.length > 0) rows.push(row);
      }
    } else {
      for (let i = 0; i < entries.length; i += numCols) {
        const row = entries.slice(i, i + numCols).map(e => ({
          label: String(e.label),
          value: String(e.value),
        }));
        rows.push(row);
      }
    }
  
    // --- Measure label widths per column ---
    const columnLabelWidths: number[] = new Array(numCols).fill(0);
    rows.forEach(row => {
      row.forEach((entry, colIdx) => {
        const width = doc.getTextWidth(`${entry.label}: `);
        if (width > columnLabelWidths[colIdx]) {
          columnLabelWidths[colIdx] = width;
        }
      });
    });
  
    // --- Render all rows ---
    for (const row of rows) {
      let colX = leftMargin;
      const lineGroups: string[][] = [];
      let maxLinesInRow = 1;
  
      // Preprocess each cell to compute wrapping lines
      row.forEach((entry, colIdx) => {
        const label = entry.label.length > 0 ? `${entry.label}: ` : '';
        const labelWidth = columnLabelWidths[colIdx];
        const availableWidth = columnWidth - labelWidth - labelValueSpacing - wrapBuffer;
  
        const lines = doc.splitTextToSize(entry.value ?? '', availableWidth);
        lineGroups.push(lines);
        if (lines.length > maxLinesInRow) {
          maxLinesInRow = lines.length;
        }
      });
  
      const estimatedHeight = maxLinesInRow * lineHeight;
      this.addNewPageIfNeeded(estimatedHeight);
  
      // Render line by line
      for (let lineIdx = 0; lineIdx < maxLinesInRow; lineIdx++) {
        colX = leftMargin;
  
        row.forEach((entry, colIdx) => {
          const label = entry.label.length > 0 ? `${entry.label}: ` : '';
          const labelWidth = columnLabelWidths[colIdx];
          const lines = lineGroups[colIdx];
          const valueLine = lines[lineIdx] ?? '';

          const lineCount = lines.length;

          const cellHeight = lineCount * lineHeight + rowSpacing;
          // const cellTopY = this.context.currentY - (lineIdx * lineHeight);
          const cellTopY = this.context.currentY - (cellHeight / 2);


          // Draw border only once (first line)
          if (options?.drawBorders && lineIdx === 0) {
            const cellWidth = columnWidth;
            doc.rect(colX, cellTopY, cellWidth - borderColumnSpacing, cellHeight);

          }

          if (options?.drawInnerDivider) {
            const dividerX = colX + labelWidth + labelValueSpacing - 1;
            doc.line(dividerX, cellTopY, dividerX, cellTopY + cellHeight);
          }

          // const verticalAlign = options?.drawBorders ? rowSpacing * 0.33 : 0;

  
          if (lineIdx === 0 && label.length > 0) {
            doc.setFont(...subheadingFont);
            doc.text(label, colX + paddingLeft, this.context.currentY + verticalAlign);
          }
  
          doc.setFont(...inputTextFont);
          const valueX = colX + labelWidth + labelValueSpacing + paddingLeft;
          doc.text(valueLine, valueX, this.context.currentY + verticalAlign);
  
          colX += columnWidth;
        });
  
        this.context.currentY += lineHeight;
      }
  
      // Add spacing between rows
      // this.context.currentY += rowSpacing;
      this.context.currentY += rowSpacing;
    }

    this.context.currentY += verticalAlign;
  }

  public renderGroupedLabelValueRowsWithHeader(
    groups: {
      header: string;
      values: { label: string; value: string }[];
      footer?: string;
    }[],
    options?: {
      columnWidths?: number[];
      lineHeight?: number;
      rowSpacing?: number;
      labelValueSpacing?: number;
      paddingLeft?: number;
      labelFontSylte?: string;
    }
  ): void {
    const { doc, leftMargin, inputFontSize, inputTextFont, subheadingFont } = this.context;

    const lineHeight = options?.lineHeight ?? 6;
    const rowSpacing = options?.rowSpacing ?? 3;
    const paddingLeft = options?.paddingLeft ?? 0;
    const labelValueSpacing = options?.labelValueSpacing ?? 4;

    doc.setFontSize(inputFontSize);

    for (const group of groups) {
      const hasAnyValue = group.values?.some(v => !!(v.value && v.value.trim()));
      const hasFooter = !!(group.footer && group.footer.trim());

      // If no value lines and no footer, skip the entire group
      if (!hasAnyValue && !hasFooter) {
        continue;
      }
      
      this.addNewPageIfNeeded(lineHeight);
      doc.setFont(...subheadingFont);
      doc.text(group.header, leftMargin + paddingLeft, this.context.currentY);
      this.context.currentY += lineHeight;

      const entries = group.values;
      const numCols = entries.length;
      const columnWidths = options?.columnWidths ?? new Array(numCols).fill(90);

      if (hasAnyValue) {
          // --- Measure label widths ---
        const labelWidths = entries.map((entry, idx) =>
          doc.getTextWidth(entry.label ? `${entry.label}: ` : '')
        );

        // --- Calculate max number of lines needed in any column ---
        const allLines: string[][] = [];

        let maxLinesInRow = 1;

        entries.forEach((entry, colIdx) => {
          const labelWidth = labelWidths[colIdx];
          const availableWidth = columnWidths[colIdx] - labelWidth - labelValueSpacing;
          const lines = doc.splitTextToSize(entry.value ?? '', availableWidth);
          allLines.push(lines);
          if (lines.length > maxLinesInRow) {
            maxLinesInRow = lines.length;
          }
        });

        const totalBlockHeight = maxLinesInRow * lineHeight + rowSpacing;
        this.addNewPageIfNeeded(totalBlockHeight);

        // --- Draw each line in each column ---
        for (let lineIdx = 0; lineIdx < maxLinesInRow; lineIdx++) {
          let colX = leftMargin + paddingLeft;

          entries.forEach((entry, colIdx) => {
            const label = entry.label ? `${entry.label}: ` : '';
            const labelWidth = labelWidths[colIdx];
            const lines = allLines[colIdx];
            const valueLine = lines[lineIdx] ?? '';

            // Print label only on first line
            if (lineIdx === 0 && label) {
              doc.setFont(...subheadingFont);
              doc.text(label, colX, this.context.currentY);
            }

            doc.setFont(...inputTextFont);
            const valueX = colX + labelWidth + labelValueSpacing;
            doc.text(valueLine, valueX, this.context.currentY);

            colX += columnWidths[colIdx];
          });

          this.context.currentY += lineHeight;
        }
      }

      // New logic: render optional footer text
      if (hasFooter) {
        const footerLines = doc.splitTextToSize(group.footer, 250); // Adjust width as needed
        footerLines.forEach(line => {
          this.addNewPageIfNeeded(lineHeight);
          doc.setFont(...inputTextFont);
          doc.text(line, leftMargin + paddingLeft + labelValueSpacing, this.context.currentY);
          this.context.currentY += lineHeight;
        });
        // this.context.currentY += rowSpacing;
      }
    }

    // Extra spacing at the end
    this.context.currentY += rowSpacing;
  }

  public renderTable(
    rows: { label: string; value: string }[],
    title?: string | null
  ): void {
    const { doc, leftMargin, headingFont, inputTextFont } = this.context;
  
    const rowHeight = 6;
    const heightNeeded = rows.length * rowHeight + 6;
  
    this.addNewPageIfNeeded(heightNeeded);
  
    doc.setFont(...headingFont);
  
    if (title) {
      doc.text(`${title}:`, leftMargin, this.context.currentY);
      this.context.currentY += 6;
    }
  
    doc.setFont(...inputTextFont);
  
    rows.forEach(row => {
      doc.text(`${row.label}: ${row.value}`, leftMargin, this.context.currentY);
      this.context.currentY += rowHeight;
    });
  
    this.addVerticalSpacing(); // should also use `this.context.currentY` internally
  }

  public renderHeading(
    text: string,
    options?: {
      bottomPadding?: number; // e.g., 0 (default), or 7, etc.
      align?: 'left' | 'center' | 'right';
    }
  ): void {
    const { doc, currentY, leftMargin, headingFont } = this.context;
    const spacing = options?.bottomPadding ?? 0;
  
    doc.setFont(...headingFont);
    doc.text(text, leftMargin, currentY, { align: options?.align ?? 'left' });
  
    this.context.currentY += spacing;
  }

  public renderSubheading(title: string): void {
    const { doc, leftMargin, inputTextFont } = this.context;
  
    const underlineY = this.context.currentY + 1.3;
  
    doc.setFont(inputTextFont[0], inputTextFont[1]);
    doc.text(title, leftMargin, this.context.currentY);
  
    const titleWidth = doc.getTextWidth(title);
    doc.line(leftMargin, underlineY, leftMargin + titleWidth, underlineY);
  
    this.context.currentY += 7;
  }

  public renderText(
    text: string,
    options?: {
      leftPadding?: number;       // extra indent, e.g. 50
      bottomPadding?: number;      // space after line, e.g. 8
      align?: 'left' | 'center' | 'right';
      font?: [string, string];    // optional override
    }
  ): void {
    const {
      doc,
      currentY,
      leftMargin,
      inputTextFont,
    } = this.context;
  
    const font = options?.font ?? inputTextFont;
    const x = leftMargin + (options?.leftPadding ?? 0);
    const spacing = options?.bottomPadding ?? 0;
    const align = options?.align ?? 'left';
  
    doc.setFont(...font);
    doc.text(text, x, currentY, { align });
  
    this.context.currentY += spacing;
  }

  public renderCheckbox(
    label: string,
    x: number,
    y: number,
    checked: boolean,
    options?: {
      checkboxSize?: number;
      font?: [string, string];
      fontSize?: number;
      labelOffset?: number;
    }
  ): void {
    const {
      doc,
      inputTextFont,
      inputFontSize,
      fontColor,
    } = this.context;
  
    const size = options?.checkboxSize ?? 3;
    const font = options?.font ?? inputTextFont;
    const fontSize = options?.fontSize ?? inputFontSize;
    const labelOffset = options?.labelOffset ?? 2;
  
    // Draw checkbox square
    doc.setDrawColor(...fontColor);
    doc.rect(x, y - size + 0.3, size, size);
  
    // Draw checkmark if checked
    if (checked) {
      doc.setFillColor(...fontColor);
      // doc.text('X', x + 0.3, y);
      doc.rect(x, y - size + 0.3, size, size, 'F'); 
    }
  
    // Draw label
    doc.setFont(...font);
    doc.setFontSize(fontSize);
    doc.setTextColor(...fontColor);
    doc.text(label, x + size + labelOffset, y);
  }

  public renderCheckboxGrid(
    entries: { label: string; checked: boolean }[],
    options?: {
      columns?: number;
      rowHeight?: number;
      colWidth?: number;
      checkboxSize?: number;
      paddingBelow?: number;
    }
  ): void {
    const { doc, currentY, leftMargin } = this.context;
  
    const columns = options?.columns ?? 5;
    const rowHeight = options?.rowHeight ?? 7;
    const colWidth = options?.colWidth ?? 40;
    const checkboxSize = options?.checkboxSize ?? 3;
  
    let x = leftMargin;
    let y = currentY;
  
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
  
      this.renderCheckbox(entry.label, x, y, entry.checked, {
        checkboxSize,
      });
  
      if ((i + 1) % columns === 0) {
        x = leftMargin;
        y += rowHeight;
      } else {
        x += colWidth;
      }
    }
  
    this.context.currentY = y + (options?.paddingBelow ?? 2);
  }

  public addNewPageIfNeeded(heightNeeded: number): void {
    const { doc, currentY, bottomMargin, footerData } = this.context;
  
    if (currentY + heightNeeded >= bottomMargin) {
      this.addFooter();
      doc.addPage();
      this.addHeader();
    }
  }

  public addFooter(): void {
    const {
      doc,
      leftMargin,
      rightMargin,
      inputTextFont,
      inputFontSize,
      fontColor,
      footerData,
    } = this.context;
  
    if (!footerData) {
      console.warn('addFooter called but footerData is undefined.');
      return;
    }
  
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const y = pageHeight - 15;
  
    const grayColor: [number, number, number] = [140, 140, 140];
  
    const nameLabel = 'Paciente:';
    const dniLabel = 'DNI:';
    const fullName = footerData.fullName?.trim() ?? '';
    const documentNumber = footerData.documentNumber?.toString() ?? '';
  
    // Set light gray bold font
    doc.setFont(inputTextFont[0], 'bold');
    doc.setFontSize(inputFontSize - 1);
    doc.setTextColor(...grayColor);
  
    // Left-aligned name
    doc.text(`${nameLabel} ${fullName}`, leftMargin, y);
  
    // Right-aligned DNI
    const dniText = `${dniLabel} ${documentNumber}`;
    const dniTextWidth = doc.getTextWidth(dniText);
    doc.text(dniText, pageWidth - rightMargin - dniTextWidth, y);
  
    // Reset font and color to default
    doc.setTextColor(...fontColor);
    doc.setFont(...inputTextFont);
    doc.setFontSize(inputFontSize);
  }

  public addHeader(title?: string | null): void {
    const {
      doc,
      inputFontSize,
      inputTextFont,
      headingFont,
      fontColor,
      titleFontSize,
      logo,
    } = this.context;

    const paddingTop = 5;
    const pageWidth = doc.internal.pageSize.getWidth();

    // Logo bounding box constraints (in mm)
    const maxImageWidth = 45;
    const maxImageHeight = 18;

    // Optional per-company scaling overrides
    const companyLogoOverrides: Record<number, { scale: number }> = {
      // Example: Company 101 wants a slightly bigger logo
      101: { scale: 1.2 },
      // Example: Company 202 wants smaller logo
      202: { scale: 0.8 },
    };

    let imageWidth = maxImageWidth;
    let imageHeight = maxImageHeight;

    if (logo?.width && logo?.height) {
      // Compute scale factors for bounding box fit
      const widthScale = maxImageWidth / logo.width;
      const heightScale = maxImageHeight / logo.height;
      let scale = Math.min(widthScale, heightScale, 1); // Never upscale

      // Apply per-client override
      // const override = companyId ? companyLogoOverrides[companyId] : undefined;
      // if (override?.scale) {
      //   scale *= override.scale;
      // }

      imageWidth = logo.width * scale;
      imageHeight = logo.height * scale;
    }

    const imageX = 10;
    const imageY = paddingTop + (maxImageHeight - imageHeight) / 2;

    // --- Draw logo ---
    if (logo) {
      doc.addImage(
        logo,
        'png',
        imageX,
        imageY,
        imageWidth,
        imageHeight
      );
    }

    // --- Draw title ---
    if (title && title.length > 0) {
      doc.setFont(headingFont[0], headingFont[1]);
      doc.setFontSize(titleFontSize);
      doc.setTextColor(...fontColor);

      const textY = paddingTop + maxImageHeight / 2 + titleFontSize / 2.8;

      doc.text(
        title,
        pageWidth / 2,
        textY,
        { align: 'center' }
      );
    }

    // --- Update currentY after header ---
    const baseY = paddingTop + maxImageHeight;
    const buffer = 8; // Space below header
    this.context.currentY = baseY + buffer;

    // --- Reset font for content ---
    doc.setFont(...inputTextFont);
    doc.setFontSize(inputFontSize);
    doc.setTextColor(...fontColor);
  }

  // public addHeader(title?: string | null): void {
  //   const {
  //     doc,
  //     inputFontSize,
  //     inputTextFont,
  //     headingFont,
  //     fontColor,
  //     titleFontSize,
  //     logo,
  //     // logoWidth = 35, // fallback if not defined
  //   } = this.context;
  
  //   const paddingTop = 5;
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   const defaultLogoWidth = 50;
  
  //   // --- Logo dimensions ---
  //   let logoAspectRatio = 1;
  //   if (logo && logo.height && logo.width) {
  //     logoAspectRatio = logo.height / logo.width;
  //   }
  
  //   const maxImageHeight = 16;
  //   const maxImageWidth = 35;
  
  //   let imageHeight = maxImageHeight;
  //   let imageWidth = logoAspectRatio > 0
  //     ? imageHeight / logoAspectRatio
  //     : defaultLogoWidth;
  
  //   if (imageWidth > maxImageWidth) {
  //     imageWidth = maxImageWidth;
  //     imageHeight = logoAspectRatio > 0
  //       ? imageWidth * logoAspectRatio
  //       : imageHeight;
  //   }
  
  //   const imageX = 10;
  
  //   // --- Draw logo ---
  //   if (logo) {
  //     doc.addImage(
  //       logo,
  //       'png',
  //       imageX,
  //       paddingTop,
  //       imageWidth,
  //       imageHeight
  //     );
  //   }
  
  //   // --- Draw title ---
  //   if (title && title.length > 0) {
  //     doc.setFont(headingFont[0], headingFont[1]);
  //     doc.setFontSize(titleFontSize);
  //     doc.setTextColor(...fontColor);
  
  //     const textY = paddingTop + (imageHeight / 2) + (titleFontSize / 2.8);
  
  //     doc.text(
  //       title,
  //       pageWidth / 2,
  //       textY,
  //       { align: 'center' }
  //     );
  //   }
  
  //   // --- Update currentY after header ---
  //   const baseY = paddingTop + imageHeight;
  //   const buffer = 8; // Space below header
  //   this.context.currentY = baseY + buffer;
  
  //   // --- Reset font for content ---
  //   doc.setFont(...inputTextFont);
  //   doc.setFontSize(inputFontSize);
  //   doc.setTextColor(...fontColor);
  // }

  public addVerticalSpacing(pixels: number = 6): void {
    const { currentY, bottomMargin } = this.context;
  
    if (currentY + pixels >= bottomMargin) {
      this.addNewPageIfNeeded(pixels);
    } else {
      this.context.currentY += pixels;
    }
  }

  public addBottomPadding(pixels: number = 3): void {
    this.context.currentY += pixels;
  }

  public async addSignatureSection(signatureData: {
    signature: string | null;
    signatureText: string;
    medicName: string;
    professionalId: string;
    date?: string;
  }): Promise<void> {

    const { doc, inputFontSize, inputTextFont, fontColor } = this.context;

    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const targetHeight = 20;
    const maxWidth     = 80;
    const gapBelowSig  = 0;   // space between bottom of signature and dotted line

    this.addNewPageIfNeeded(70); // allow a bit more vertical space

    let signatureStartX = 0;
    let signatureTopY   = pageHeight - 80; // temporary; will recompute
    let signatureHeight = 0;

    if (signatureData.signature) {
      try {
        const result = await this.pdfUtilities.createSignature(
          signatureData.signature,
          targetHeight,
          maxWidth,
          12,     // minHeight
          50      // minWidth
        );

        signatureHeight = result.height;

        // place dotted line first conceptually, then place signature above it
        const lineY = pageHeight - 40; // fixed distance from bottom of page
        signatureTopY = lineY - gapBelowSig - signatureHeight;

        signatureStartX = (pageWidth - result.width) / 2;

        doc.addImage(
          signatureData.signature,
          'PNG',
          signatureStartX,
          signatureTopY,
          result.width,
          result.height
        );

        // Now draw dotted line
        this.drawSignatureLineAndText(
          doc,
          pageWidth,
          lineY,
          inputFontSize,
          inputTextFont,
          fontColor,
          signatureData
        );

        return;
      } catch (err) {
        console.error('Error loading signature image:', err);
      }
    }

    // Fallback: no image → handwritten-style font, keep old layout
    const lineY = pageHeight - 40;

    doc.setFont('Pacifico-Regular');
    doc.setFontSize(18);
    doc.setTextColor(...fontColor);
    doc.text(signatureData.signatureText ?? '', pageWidth / 2, lineY - 10, { align: 'center' });

    this.drawSignatureLineAndText(
      doc,
      pageWidth,
      lineY,
      inputFontSize,
      inputTextFont,
      fontColor,
      signatureData
    );
  }

  // helper for dotted line + name + colegiatura/date
  private drawSignatureLineAndText(
    doc: jsPDF,
    pageWidth: number,
    lineY: number,
    inputFontSize: number,
    inputTextFont: [string, string] | string[],
    fontColor: number[],
    signatureData: { medicName: string; professionalId: string; date?: string }
  ) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(inputFontSize);
    doc.setTextColor(fontColor[0], fontColor[1], fontColor[2]);

    const dottedLine     = '.'.repeat(78);
    const dottedLineWidth = doc.getTextWidth(dottedLine);
    const lineStartX      = (pageWidth - dottedLineWidth) / 2;

    doc.text(dottedLine, lineStartX, lineY);

    const innerLeftX  = lineStartX;
    const innerRightX = lineStartX + dottedLineWidth;

    doc.setFont(inputTextFont[0], inputTextFont[1]);
    doc.text(signatureData.medicName, pageWidth / 2, lineY + 6, { align: 'center' });

    const cmpText     = `Colegiatura: ${signatureData.professionalId}`;
    const dateLabel   = `Fecha: ${signatureData.date ?? ''}`;
    const dateTextWidth = doc.getTextWidth(dateLabel);

    doc.text(cmpText, innerLeftX, lineY + 12);
    doc.text(dateLabel, innerRightX - dateTextWidth, lineY + 12);
  }

  // public async addSignatureSection(signatureData: {
  //   signature: string | null;
  //   signatureText: string;
  //   medicName: string;
  //   professionalId: string;
  //   date?: string;
  // }): Promise<void> {
  //   const {
  //     doc,
  //     inputFontSize,
  //     inputTextFont,
  //     fontColor,
  //   } = this.context;
  
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   const pageHeight = doc.internal.pageSize.getHeight();

  //   const targetHeight = 20;
  //   const maxWidth = 80;
  //   const gapBelowSig = 6;

  //   const centerY = pageHeight - 65;
  //   const lineY = centerY + 24;

  //   this.addNewPageIfNeeded(70);

  //   let signatureStartX = (pageWidth - maxWidth) / 2;

  //   if (signatureData.signature) {
  //     try {
  //       const result = await this.pdfUtilities.createSignature(
  //         signatureData.signature,
  //         targetHeight,
  //         maxWidth, 
  //       );

  //       signatureStartX = (pageWidth - result.width) / 2;  // ⬅ use result.width

  //       doc.addImage(
  //         signatureData.signature,
  //         'PNG',
  //         signatureStartX,
  //         centerY,
  //         result.width,
  //         result.height
  //       );
  //     } catch (err) {
  //       console.error('Error loading signature image:', err);
  //     }
  //   } else {
  //     doc.setFont('Pacifico-Regular');
  //     doc.setFontSize(18);
  //     doc.setTextColor(...fontColor);
  //     doc.text(signatureData.signatureText ?? '', pageWidth / 2, centerY + 20, { align: 'center' });
  //   }
  
  //   // Dotted line
  //   doc.setFont('helvetica', 'normal');
  //   doc.setFontSize(inputFontSize);
  //   doc.setTextColor(...fontColor);
    
  //   const dottedLine = '.'.repeat(78);
  //   const dottedLineWidth = doc.getTextWidth(dottedLine);
  //   const lineStartX = (pageWidth - dottedLineWidth) / 2;

  //   doc.text(dottedLine, lineStartX, lineY);
  
  //   const innerLeftX = lineStartX;
  //   const innerRightX = lineStartX + dottedLineWidth;
  
  //   // Doctor's name
  //   doc.setFont(...inputTextFont);
  //   doc.setFontSize(inputFontSize);
  //   doc.text(signatureData.medicName, pageWidth / 2, lineY + 6, { align: 'center' });
  
  //   // Colegiatura + Date
  //   const cmpText = `Colegiatura: ${signatureData.professionalId}`;
  //   const dateLabel = `Fecha: ${signatureData.date ?? ''}`;
  //   const dateTextWidth = doc.getTextWidth(dateLabel);
  
  //   doc.text(cmpText, innerLeftX, lineY + 12);
  //   doc.text(dateLabel, innerRightX - dateTextWidth, lineY + 12);
  // }

  public setHeadingFont(): void {
    this.context.doc.setFont(...this.context.headingFont);
  }
  
  public setSubheadingFont(): void {
    this.context.doc.setFont(...this.context.subheadingFont);
  }
  
  public setInputFont(): void {
    this.context.doc.setFont(...this.context.inputTextFont);
  }
  
  public setFont(font: [string, string]): void {
    this.context.doc.setFont(font[0], font[1]);
  }

  public setFooter(data: { fullName: string; documentNumber: string }): void {
    this.context.footerData = data;
  }
}
